<template>
  <v-container id="regular-forms" fluid tag="section">
    <v-row>
      <v-col cols="12">
        <base-material-card
          color="primary"
          icon="mdi-file-document"
          :title="$route.params.id ? $t('Edit Order') : $t('New Order')"
          class="px-5 py-3"
        >
          <v-progress-linear
            indeterminate
            rounded
            height="6"
            v-if="loading"
          ></v-progress-linear>
          <v-form
            v-else
            ref="form"
            autocomplete="new-password"
            lazy-validation
            @submit.prevent="saveItem"
          >
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="formValues.customer_id"
                  :items="customers"
                  :label="$t('customer')"
                  :item-text="
                    item =>
                      item.name +
                      (item.city ? ' (' + item.city + ')' : '') +
                      (this.allowEdit && item.member_name
                        ? ' [' + item.member_name + ']'
                        : '')
                  "
                  item-value="id"
                  v-validate=""
                  :disabled="!allowEdit"
                  data-vv-name="customer_id"
                  :error-messages="errors.collect('customer_id')"
                  @change="changeCustomer()"
                >
                  <slot name="append-outer">
                    <v-icon small class="mr-2">
                      mdi-pencil
                    </v-icon>
                  </slot>
                </v-autocomplete>
              </v-col>
              <v-col>
                <v-select
                  v-model="formValues.country_id"
                  :items="countries"
                  :label="$t('country')"
                  item-text="name"
                  item-value="id"
                  v-validate=""
                  data-vv-name="country_id"
                  :error-messages="errors.collect('country_id')"
                  :disabled="formValues.has_orderlines"
                ></v-select>
              </v-col>
              <v-col v-if="formValues.number">
                <v-text-field
                  v-model="formValues.number"
                  :label="$t('number')"
                  disabled
                ></v-text-field>
              </v-col>
              <v-col v-if="formValues.id && formValues.state">
                <v-text-field
                  :value="$t(formValues.state.name)"
                  :label="$t('state')"
                  disabled
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="formValues.project"
                  :label="$t('project')"
                  v-validate=""
                  data-vv-name="project"
                  :error-messages="errors.collect('project')"
                  :disabled="!allowEdit"
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="formValues.project_address"
                  :label="$t('project_address')"
                  v-validate=""
                  data-vv-name="project_address"
                  :error-messages="errors.collect('project_address')"
                  :disabled="!allowEdit"
                ></v-text-field>
              </v-col>
              <v-col v-if="formValues.id">
                <v-text-field
                  v-model="formValues.reference_number_customer"
                  :label="$t('reference number customer')"
                  v-validate=""
                  data-vv-name="reference_number_customer"
                  :error-messages="errors.collect('reference_number_customer')"
                  :disabled="!allowEdit"
                ></v-text-field>
              </v-col>
              <v-col v-if="formValues.id">
                <v-select
                  v-model="formValues.label_delivery"
                  :items="labelDelivery"
                  :label="$t('label_delivery')"
                  item-text="name"
                  item-value="id"
                  v-validate=""
                  data-vv-name="label_delivery"
                  :error-messages="errors.collect('label_delivery')"
                ></v-select>
              </v-col>
              <v-col v-if="formValues.id">
                <v-text-field
                  :value="formValues.created_at | formatDateTime"
                  :label="$t('created')"
                  disabled
                ></v-text-field>
              </v-col>
              <v-col v-if="formValues.id">
                <v-text-field
                  :value="formValues.updated_at | formatDateTime"
                  :label="$t('edited')"
                  disabled
                ></v-text-field>
              </v-col>
              <v-col v-if="formValues.id && formValues.confirmed_at">
                <v-text-field
                  :value="formValues.confirmed_at | formatDate"
                  :label="$t('confirmed_at')"
                  disabled
                ></v-text-field>
              </v-col>
              <v-col
                v-if="
                  formValues.id &&
                    formValues.confirmed_at &&
                    formValues.state_id > 2
                "
              >
                <v-checkbox
                  v-model="formValues.downloaded"
                  :label="$t('certificates_downloaded')"
                  v-validate=""
                  data-vv-name="downloaded"
                  :error-messages="errors.collect('downloaded')"
                  @change="saveDownloaded()"
                ></v-checkbox>
              </v-col>
            </v-row>
            <template v-if="hasProductionNotification">
              <v-card class="grey lighten-4 pa-2">
                <v-card-title>
                  {{ $t("production_notification") }}
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="formValues.processing_person_name"
                        :label="$t('processing_person_name') + '*'"
                        v-validate=""
                        data-vv-name="processing_person_name"
                        :error-messages="
                          errors.collect('processing_person_name')
                        "
                        :disabled="!allowEdit"
                      ></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field
                        v-model="formValues.processing_person_phone"
                        :label="$t('processing_person_phone') + '*'"
                        v-validate=""
                        data-vv-name="processing_person_phone"
                        :error-messages="
                          errors.collect('processing_person_phone')
                        "
                        :disabled="!allowEdit"
                      ></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field
                        v-model="formValues.production_week"
                        :label="$t('production_week') + '*'"
                        v-validate=""
                        data-vv-name="production_week"
                        :error-messages="errors.collect('production_week')"
                        :disabled="!allowEdit"
                        hint="MM/JJJJ"
                        persistent-hint
                      ></v-text-field>
                    </v-col>

                    <v-col>
                      <v-text-field
                        v-model="formValues.producttion_notification_typename"
                        :label="$t('producttion_notification_typename')"
                        :placeholder="
                          $t('producttion_notification_typename_default')
                        "
                        persistent-placeholder
                        v-validate=""
                        data-vv-name="producttion_notification_typename"
                        :error-messages="
                          errors.collect('producttion_notification_typename')
                        "
                        :disabled="!allowEdit"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <!-- <v-col>
                      <v-text-field
                        v-model="formValues.mounting_company_name"
                        :label="$t('mounting_company_name') + '*'"
                        v-validate="required"
                        data-vv-name="mounting_company_name"
                        :error-messages="
                          errors.collect('mounting_company_name')
                        "
                        :disabled="!allowEdit"
                      ></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field
                        v-model="formValues.mounting_company_address"
                        :label="$t('mounting_company_address') + '*'"
                        v-validate="required"
                        data-vv-name="mounting_company_address"
                        :error-messages="
                          errors.collect('mounting_company_address')
                        "
                        :disabled="!allowEdit"
                      ></v-text-field>
                    </v-col> -->
                    <v-col>
                      <v-text-field
                        v-model="formValues.mounting_person_name"
                        :label="$t('mounting_person_name') + '*'"
                        v-validate=""
                        data-vv-name="mounting_person_name"
                        :error-messages="errors.collect('mounting_person_name')"
                        :disabled="!allowEdit"
                      ></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field
                        v-model="formValues.mounting_person_phone"
                        :label="$t('mounting_person_phone') + '*'"
                        v-validate=""
                        data-vv-name="mounting_person_phone"
                        :error-messages="
                          errors.collect('mounting_person_phone')
                        "
                        :disabled="!allowEdit"
                      ></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field
                        v-model="formValues.mounting_week"
                        :label="$t('mounting_week') + '*'"
                        v-validate=""
                        data-vv-name="mounting_week"
                        :error-messages="errors.collect('mounting_week')"
                        :disabled="!allowEdit"
                        hint="MM/JJJJ"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col></v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>

            <v-tabs
              v-model="tab"
              v-if="formValues.id"
              background-color="gray"
              dark
              right
            >
              <v-tab key="order">{{ $t("order") }}</v-tab>
              <v-tab key="items" @click="getOrderItems">{{
                $t("products")
              }}</v-tab>
              <v-tab key="documents">{{ $t("documents") }}</v-tab>
              <v-tab
                v-if="
                  formValues.state_id >= 3 &&
                    this.$auth.user().permissions.includes('order.set_payed')
                "
                key="payment"
                >{{ $t("payment") }}</v-tab
              >
              <v-tabs-items v-model="tab">
                <v-tab-item key="order">
                  <v-card>
                    <Orderlines
                      :beforeCreate="saveItem"
                      :order="formValues"
                      :getHandbooks="getHandbooks"
                      v-bind:orderLines.sync="orderLines"
                    ></Orderlines>
                  </v-card>
                </v-tab-item>
                <v-tab-item key="items">
                  <v-card>
                    <v-progress-linear
                      indeterminate
                      rounded
                      height="6"
                      v-if="loading_articles"
                    ></v-progress-linear>
                    <v-flex v-else-if="orderItems">
                      <v-flex
                        v-for="(orderItem, index) in orderItems"
                        :key="'orderitem' + index"
                      >
                        <v-card class="grey lighten-2 pa-2">
                          <v-card-text>
                            <v-row>
                              <v-col>
                                <h3>
                                  {{ $t("Doortype") }} {{ orderItem.code }}
                                </h3>
                                <h4>{{ $t("positions") }}:</h4>
                                <p>{{ orderItem.position }}</p>
                              </v-col>

                              <v-col v-if="orderItem.icon">
                                <h4 mt-3>{{ $t("icon") }}</h4>
                                <v-img
                                  :src="orderItem.icon | addToken"
                                  max-height="75"
                                  max-width="75"
                                  contain
                                ></v-img>
                              </v-col>
                            </v-row>
                            <v-row
                              v-if="
                                orderItem.images.length ||
                                  orderItem.sketches.length
                              "
                            >
                              <v-col>
                                <v-card v-if="orderItem.sketches.length">
                                  <v-system-bar lights-out
                                    ><h3>{{ $t("sketches") }}</h3></v-system-bar
                                  >
                                  <v-carousel
                                    :continuous="false"
                                    show-arrows
                                    hide-delimiters
                                    height="300"
                                  >
                                    <v-carousel-item
                                      v-for="(sketch, i) in orderItem.sketches"
                                      :key="i"
                                    >
                                      <v-sheet height="100%" tile>
                                        <v-row
                                          class="fill-height"
                                          align="center"
                                          justify="center"
                                        >
                                          <div class="display-3">
                                            <a
                                              @click="showImgSketches(index, i)"
                                            >
                                              <v-img
                                                v-if="sketch.thumb"
                                                :src="sketch.thumb | addToken"
                                                height="300"
                                                width="500"
                                              ></v-img>
                                              <v-icon v-else>mdi-file</v-icon>
                                            </a>
                                          </div>
                                        </v-row>
                                      </v-sheet>
                                    </v-carousel-item>
                                  </v-carousel>
                                  <vue-easy-lightbox
                                    :visible="visible_sketches[index]"
                                    :imgs="addToken(orderItem)"
                                    :index="index_sketches[index]"
                                    @hide="handleHide(index)"
                                  ></vue-easy-lightbox>
                                </v-card>
                                <!-- <v-card v-else>
                                  <v-alert type="info"
                                    ><span class="white--text">{{
                                      $t("no-sketches")
                                    }}</span></v-alert
                                  >
                                </v-card> -->
                              </v-col>
                              <v-col>
                                <v-card v-if="orderItem.images.length">
                                  <v-system-bar lights-out
                                    ><h3>{{ $t("images") }}</h3></v-system-bar
                                  >
                                  <v-carousel
                                    :continuous="false"
                                    :show-arrows="false"
                                    height="200"
                                  >
                                    <v-carousel-item
                                      v-for="(sketch, i) in orderItem.images"
                                      :key="i"
                                    >
                                      <v-sheet height="100%" tile>
                                        <v-row
                                          class="fill-height"
                                          align="center"
                                          justify="center"
                                        >
                                          <div class="display-3">
                                            <a @click="showImgImages(index, i)">
                                              <v-img
                                                v-if="sketch.thumb"
                                                :src="sketch.thumb | addToken"
                                                height="200"
                                                width="300"
                                              ></v-img>
                                              <v-icon v-else>mdi-file</v-icon>
                                            </a>
                                          </div>
                                        </v-row>
                                      </v-sheet>
                                    </v-carousel-item>
                                  </v-carousel>
                                  <vue-easy-lightbox
                                    :visible="visible_images[index]"
                                    :imgs="orderItem.images"
                                    :index="index_images[index]"
                                    @hide="handleHide(index)"
                                  ></vue-easy-lightbox>
                                </v-card>
                                <!-- <v-card v-else>
                                  <v-alert type="info"
                                    ><p class="white--text">
                                      {{ $t("no-images") }}
                                    </p></v-alert
                                  >
                                </v-card> -->
                              </v-col>
                            </v-row>

                            <v-simple-table dense>
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th class="text-left">{{ $t("usage") }}</th>
                                    <th class="text-left">{{ $t("name") }}</th>
                                    <th class="text-left">
                                      {{ $t("producer") }}
                                    </th>
                                    <th class="text-left">
                                      {{ $t("supplier") }}
                                    </th>
                                    <th class="text-left">
                                      {{ $t("documents") }}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr
                                    v-for="(category,
                                    index) in orderItem.categories"
                                    :key="'categories' + index"
                                  >
                                    <td>{{ category.name }}</td>
                                    <td
                                      v-if="
                                        category.item &&
                                          category.item.name != null
                                      "
                                    >
                                      <a
                                        v-if="$auth.check('order.editall')"
                                        target="_blank"
                                        :href="'/item/' + category.item.id"
                                        >{{ category.item.name }}</a
                                      >
                                      <span v-else>{{
                                        category.item.name
                                      }}</span>
                                      <span v-if="category.required"> *</span>
                                      <div
                                        v-if="category.item.sub_items.length"
                                      >
                                        {{ $t("Subitems") }}:
                                        <v-chip
                                          class="mr-1 mb-1"
                                          v-for="(subitem, index) in category
                                            .item.sub_items"
                                          :key="'subitem' + index"
                                          >{{ subitem.name }}</v-chip
                                        >
                                      </div>
                                    </td>
                                    <td v-else>-</td>
                                    <td>
                                      <a
                                        v-if="
                                          category.item &&
                                            category.item.producer_url
                                        "
                                        :href="category.item.producer_url"
                                        target="_blank"
                                      >
                                        <span
                                          v-if="
                                            category.item &&
                                              category.item.producer
                                          "
                                          >{{ category.item.producer }}</span
                                        >
                                        <span v-else>{{ $t("producer") }}</span>
                                      </a>
                                      <p v-else>
                                        <span
                                          v-if="
                                            category.item &&
                                              category.item.producer
                                          "
                                          >{{ category.item.producer }}</span
                                        >
                                        <span v-else>{{ $t("producer") }}</span>
                                      </p>
                                    </td>
                                    <td>
                                      <template
                                        v-if="
                                          category.item &&
                                            category.item.suppliers
                                        "
                                      >
                                        <v-chip
                                          class="mr-1 mb-1 pa-2"
                                          v-for="supplier in category.item
                                            .suppliers"
                                          :key="supplier.id"
                                        >
                                          <span
                                            v-if="
                                              suppliers.find(_supplier => {
                                                return (
                                                  _supplier.id == supplier.id
                                                );
                                              }) &&
                                                suppliers.find(_supplier => {
                                                  return (
                                                    _supplier.id == supplier.id
                                                  );
                                                }).website != null
                                            "
                                          >
                                            <a
                                              target="_blank"
                                              :href="
                                                suppliers.find(_supplier => {
                                                  return (
                                                    _supplier.id == supplier.id
                                                  );
                                                }).website
                                              "
                                            >
                                              {{
                                                suppliers.find(_supplier => {
                                                  return (
                                                    _supplier.id == supplier.id
                                                  );
                                                }).name
                                              }}</a
                                            >
                                          </span>
                                          <span v-else>
                                            {{
                                              suppliers.find(_supplier => {
                                                return (
                                                  _supplier.id == supplier.id
                                                );
                                              }).name
                                            }}
                                          </span>
                                        </v-chip>
                                      </template>
                                    </td>
                                    <td>
                                      <template
                                        v-if="
                                          category.item &&
                                            category.item.documents
                                        "
                                      >
                                        <v-btn
                                          small
                                          class="ma-1"
                                          rounded
                                          v-for="document in category.item
                                            .documents"
                                          :key="document.id"
                                          :href="document.url | addToken"
                                          target="_blank"
                                        >
                                          <v-icon v-if="document.thumb"
                                            >mdi-camera</v-icon
                                          >
                                          <v-icon v-else>mdi-file</v-icon>
                                        </v-btn>
                                        <template
                                          v-if="
                                            category.item.sub_items &&
                                              category.item.sub_items
                                          "
                                        >
                                          <span
                                            v-for="(subitem, index) in category
                                              .item.sub_items"
                                            :key="index"
                                          >
                                            <v-btn
                                              class="ma-1"
                                              small
                                              rounded
                                              v-for="document in subitem.documents"
                                              :key="document.id"
                                              :href="document.url | addToken"
                                              target="_blank"
                                            >
                                              <v-icon v-if="document.thumb"
                                                >mdi-camera</v-icon
                                              >
                                              <v-icon v-else>mdi-file</v-icon>
                                            </v-btn>
                                          </span>
                                        </template>
                                      </template>
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </v-card-text>
                        </v-card>
                      </v-flex>
                      <p>* {{ $t("required articles") }}</p>
                    </v-flex>
                    <v-flex v-else>
                      <p>{{ $t("no articles found") }}</p>
                    </v-flex>
                  </v-card>
                </v-tab-item>
                <v-tab-item key="documents">
                  <v-card>
                    <h3 class="mb-3">{{ $t("documents") }}</h3>
                    <p
                      v-if="
                        formValues.state_id >= 3 &&
                          orderLines.length &&
                          !deliveryNoteExists
                      "
                      class="mb-3"
                    >
                      <v-btn
                        color="primary"
                        :loading="generatingDeliveryNote"
                        @click="generateDeliveryNote"
                      >
                        {{ $t("generate_delivery_note") }}
                      </v-btn>
                    </p>
                    <template
                      v-if="formValues.documents && formValues.documents.length"
                    >
                      <v-card>
                        <v-card-text>
                          <v-simple-table>
                            <thead>
                              <tr>
                                <th class="text-left">
                                  Name
                                </th>

                                <th class="text-left">
                                  Download
                                </th>
                                <th class="text-left">
                                  Date
                                </th>
                                <th
                                  v-if="
                                    $auth
                                      .user()
                                      .permissions.includes('doorType.update')
                                  "
                                  class="text-right"
                                ></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="item in formValues.documents"
                                :key="item.id"
                              >
                                <td>
                                  {{ $t(item.name) }}
                                  <template v-if="item.positions"
                                    >[{{ $t("positions") }}:
                                    {{ item.positions }}]</template
                                  >
                                </td>
                                <td>
                                  <v-btn
                                    small
                                    primary
                                    target="_blank"
                                    :href="
                                      ('/orderFiles/' + item.id) | addToken
                                    "
                                    >DOWNLOAD</v-btn
                                  >
                                </td>
                                <td>{{ item.created_at | formatDateTime }}</td>
                                <td
                                  v-if="
                                    $auth
                                      .user()
                                      .permissions.includes('doorType.update')
                                  "
                                >
                                  <v-btn
                                    target="_blank"
                                    :href="
                                      item.original_document_url | addToken
                                    "
                                    fab
                                    x-small
                                    class="mx-2"
                                    ><v-icon>mdi-file-find</v-icon></v-btn
                                  >
                                  <v-btn
                                    @click="regenerateDocument(item.id)"
                                    fab
                                    x-small
                                    class="mx-2"
                                    ><v-icon>mdi-reload</v-icon></v-btn
                                  >
                                  <v-btn
                                    @click="
                                      toggleDocumentInternal(
                                        item.id,
                                        !item.internal
                                      )
                                    "
                                    fab
                                    x-small
                                    class="mx-2"
                                    :color="
                                      item.internal ? 'primary' : undefined
                                    "
                                    ><v-icon>mdi-lock</v-icon></v-btn
                                  >
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                      <p
                        v-if="
                          orderLines.filter(orderLine => {
                            if (
                              orderLine.door_type.typeCategory
                                .has_performance ||
                              orderLine.door_type.typeCategory.has_ce ||
                              orderLine.door_type.typeCategory.has_ce_heat
                            ) {
                              return 1;
                            }
                            return '';
                          }).length
                        "
                      >
                        <small>{{ $t("entrydoor_certificates_info") }}</small>
                      </p>
                      <br /><br />
                    </template>
                    <div v-if="formValues.id">
                      <template v-if="!formValues.documents.length">
                        <div
                          v-if="
                            computedFormValues.country_id == 1 &&
                              orderLines.filter(orderLine => {
                                if (
                                  orderLine.door_type.typeCategory
                                    .has_conformity
                                ) {
                                  return 1;
                                }
                                return '';
                              }).length
                          "
                        >
                          <h4 class="mt-4 mb-2">
                            {{ $t("fire_certificates") }}
                          </h4>
                          <p v-if="hasConformity">
                            <v-btn
                              :href="
                                ('/order/' + formValues.id + '/conformity')
                                  | addToken
                              "
                              target="_blank"
                              >{{ $t("conformity") }} KOBRA
                            </v-btn>
                          </p>
                          <p v-if="hasMoraltConformity">
                            <v-btn
                              :href="
                                '/order/' +
                                  formValues.id +
                                  '/conformity-moralt | addToken'
                              "
                              target="_blank"
                              >{{ $t("conformity") }} MORALT
                            </v-btn>
                          </p>
                          <hr />
                        </div>
                        <div
                          v-if="
                            orderLines.filter(orderLine => {
                              if (
                                orderLine.door_type.typeCategory
                                  .has_performance ||
                                orderLine.door_type.typeCategory.has_ce ||
                                orderLine.door_type.typeCategory.has_ce_heat
                              ) {
                                return 1;
                              }
                              return '';
                            }).length
                          "
                        >
                          <h4 class="mt-4 mb-2">
                            {{ $t("entrydoor_certificates") }}
                          </h4>
                          <p>
                            <small>{{
                              $t("entrydoor_certificates_info")
                            }}</small>
                          </p>
                          <p
                            v-if="
                              orderLines.filter(orderLine => {
                                if (
                                  orderLine.door_type.typeCategory.has_ce_heat
                                ) {
                                  return 1;
                                }
                                return '';
                              }).length
                            "
                          >
                            <v-btn
                              :href="
                                ('/order/' + formValues.id + '/ce-heat')
                                  | addToken
                              "
                              target="_blank"
                              >{{ $t("ce-heat") }}</v-btn
                            >
                          </p>
                          <p
                            v-if="
                              orderLines.filter(orderLine => {
                                if (
                                  orderLine.door_type.typeCategory
                                    .has_performance
                                ) {
                                  return 1;
                                }
                                return '';
                              }).length
                            "
                          >
                            <v-btn
                              :href="
                                ('/order/' + formValues.id + '/performance')
                                  | addToken
                              "
                              target="_blank"
                              >{{ $t("performance") }}</v-btn
                            >
                          </p>
                          <p
                            v-if="
                              orderLines.filter(orderLine => {
                                if (orderLine.door_type.typeCategory.has_ce) {
                                  return 1;
                                }
                                return '';
                              }).length
                            "
                          >
                            <v-btn
                              :href="
                                ('/order/' + formValues.id + '/ce') | addToken
                              "
                              target="_blank"
                              >{{ $t("ce") }}</v-btn
                            >
                          </p>

                          <hr />
                        </div>
                        <template
                          v-if="
                            this.production_notifications &&
                              hasProductionNotification
                          "
                        >
                          <hr />
                          <h4 class="mt-4 mb-2">
                            {{ $t("production_notification") }}
                          </h4>
                          <p>
                            <v-btn
                              :href="
                                ('/order/' +
                                  formValues.id +
                                  '/production-notification')
                                  | addToken
                              "
                              target="_blank"
                              >{{ $t("download") }}</v-btn
                            >
                          </p>
                        </template>
                        <h3 class="mt-4 mb-2">{{ $t("handbooks") }}</h3>
                        <div v-if="handbooks">
                          <p v-for="subitem in handbooks" :key="subitem.id">
                            <v-btn
                              :href="
                                ('/order/' +
                                  formValues.id +
                                  '/handbook/' +
                                  subitem.id)
                                  | addToken
                              "
                              target="_blank"
                            >
                              {{ $t("handbook") }} [{{ subitem.name }}] |
                              {{ $t("positions") }}:
                              {{ subitem.positions.join(", ") }}
                            </v-btn>
                          </p>
                        </div>
                        <hr />
                      </template>
                      <h4 class="mt-4 mb-2">{{ $t("documents") }}</h4>
                      <p>
                        <v-btn
                          :href="
                            ('/order/' + formValues.id + '/checklist')
                              | addToken
                          "
                          target="_blank"
                          >{{ $t("checklist") }}</v-btn
                        >
                      </p>
                      <p>
                        <v-btn
                          :href="
                            ('/order/' + formValues.id + '/specification/de')
                              | addToken
                          "
                          target="_blank"
                          >{{ $t("specification") }} DE</v-btn
                        >
                      </p>
                      <p>
                        <v-btn
                          :href="
                            ('/order/' + formValues.id + '/specification/it')
                              | addToken
                          "
                          target="_blank"
                          >{{ $t("specification") }} IT</v-btn
                        >
                      </p>
                      <div v-if="computedFormValues.country_id == 1">
                        <p
                          v-for="(item, index) in this.computedDocuments"
                          :key="index"
                        >
                          <v-btn :href="item.url | addToken" target="_blank"
                            >{{ item.name }} | {{ $t("positions") }}:
                            {{ item.positions.join(", ") }}</v-btn
                          >
                        </p>
                      </div>
                      <div v-else>
                        <hr />
                        <h4 class="mt-4 mb-2">{{ $t("documents_abroad") }}</h4>
                        <p
                          v-for="(item, index) in this.computedDocumentsAbroad"
                          :key="index"
                        >
                          <v-btn :href="item.url | addToken" target="_blank"
                            >{{ item.name }} | {{ $t("positions") }}:
                            {{ item.positions.join(", ") }}</v-btn
                          >
                        </p>
                      </div>

                      <hr />
                      <h4 class="mt-4 mb-2">{{ $t("drawings") }}</h4>
                      <p
                        v-for="(item, index) in this.computedDrawings"
                        :key="index"
                      >
                        <v-btn :href="item.url | addToken" target="_blank"
                          >{{ item.name }} | {{ $t("positions") }}:
                          {{ item.positions.join(", ") }}</v-btn
                        >
                      </p>
                    </div>
                  </v-card>
                </v-tab-item>
                <v-tab-item
                  v-if="
                    formValues.state_id >= 3 &&
                      this.$auth.user().permissions.includes('order.set_payed')
                  "
                  key="payment"
                >
                  <v-card>
                    <h3 class="mb-3">{{ $t("payment") }}</h3>
                    <v-container>
                      <v-row>
                        <v-data-table
                          :headers="paymentHeaders"
                          :items="formValues.payment_positions"
                          class="elevation-1 mb-2"
                          hide-default-footer
                          disable-pagination
                          v-if="formValues"
                        >
                          <template v-slot:item.price="{ item }">
                            {{ item.price }} €
                          </template>
                          <template v-slot:item.totalPrice="{ item }">
                            {{ item.price * item.quantity }} €
                          </template>
                        </v-data-table>
                      </v-row>
                      <v-row>
                        <v-col>
                          <p v-if="formValues.state_id == 3">
                            {{ $t("no_payments") }}
                          </p>
                          <div v-else-if="formValues.state_id == 4">
                            <p>
                              <b>{{ $t("total_amount") }}:</b>
                              {{ formValues.total_price }} €
                            </p>
                            <p>
                              <b>{{ $t("payed_amount") }}:</b>
                              {{ formValues.payed_amount }} €
                            </p>
                            <v-btn
                              v-if="
                                formValues.state_id == 4 &&
                                  this.$auth
                                    .user()
                                    .permissions.includes('order.set_payed')
                              "
                              color="primary"
                              class="mr-4"
                              @click="openPaymentAmountDialog"
                            >
                              {{ $t("set_paymentamount") }}
                            </v-btn>
                            <v-btn
                              v-if="
                                formValues.state_id == 4 &&
                                  this.$auth
                                    .user()
                                    .permissions.includes('order.set_payed')
                              "
                              color="primary"
                              class="mr-4"
                              @click="setOrderSend"
                            >
                              {{ $t("order_send") }}
                            </v-btn>
                            <v-dialog
                              v-model="paymentAmountDialog"
                              :overlay="false"
                              max-width="500px"
                              transition="dialog-transition"
                            >
                              <v-card>
                                <v-card-title>{{
                                  $t("set_amount")
                                }}</v-card-title>
                                <v-card-text>
                                  <v-text-field
                                    :label="$t('amount')"
                                    v-model="payed_amount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                  />
                                </v-card-text>
                                <v-card-actions>
                                  <v-spacer></v-spacer>
                                  <v-btn
                                    color="primary"
                                    class="mr-4"
                                    @click="savePaymentAmount"
                                  >
                                    {{ $t("save") }}
                                  </v-btn>
                                </v-card-actions>
                              </v-card>
                            </v-dialog>
                          </div>

                          <v-btn
                            v-if="
                              formValues.state_id == 3 &&
                                this.$auth
                                  .user()
                                  .permissions.includes('orderPosition.create')
                            "
                            color="primary"
                            class="mr-4"
                            @click="createPayment"
                          >
                            {{ $t("create payments") }}
                          </v-btn>
                        </v-col>
                        <v-dialog
                          v-model="paymentDialog"
                          :overlay="false"
                          max-width="900px"
                          transition="dialog-transition"
                        >
                          <v-card>
                            <v-card-title>{{
                              $t("Create paymentlines")
                            }}</v-card-title>
                            <v-card-text>
                              <v-data-table
                                :headers="paymentHeaders"
                                :items="paymentPositions"
                                :loading="paymentLoading"
                                :multi-sort="true"
                                class="elevation-1"
                                hide-default-footer
                                disable-pagination
                              >
                                <template v-slot:item.quantity="{ item }">
                                  <v-text-field
                                    v-model="item.quantity"
                                    type="number"
                                    :disabled="!item.quantity_editable"
                                    flat
                                  ></v-text-field>
                                </template>
                                <template v-slot:item.price="{ item }">
                                  <v-text-field
                                    v-model="item.price"
                                    type="number"
                                    :disabled="!item.editable"
                                    flat
                                  ></v-text-field> </template
                                ><template v-slot:item.totalPrice="{ item }">
                                  {{ item.price * item.quantity }}
                                </template>

                                <template v-slot:body.append="{}">
                                  <tr>
                                    <th></th>
                                    <th></th>
                                    <th>{{ $t("Total") }}</th>
                                    <th>{{ paymentTotals }}</th>
                                  </tr>
                                </template>
                              </v-data-table>
                              <v-container class="px-0" fluid>
                                <v-switch
                                  v-model="postCosts"
                                  :label="
                                    $t('post_costs') +
                                      (this.formValues.label_delivery
                                        ? ' (' + $t('label_send') + ')'
                                        : ' (' + $t('label_take') + ')')
                                  "
                                  @change="onPostCostsChange"
                                ></v-switch>
                                <v-switch
                                  v-model="printCosts"
                                  :label="$t('print_costs')"
                                  @change="onPrintCostsChange"
                                ></v-switch>
                              </v-container>
                            </v-card-text>
                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn
                                color="primary"
                                class="mr-4"
                                @click="savePayment"
                                :disabled="
                                  !paymentPositions.length || paymentLoading
                                "
                              >
                                {{ $t("save") }}
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-dialog>
                      </v-row>
                    </v-container>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-tabs>

            <v-card-actions class="pl-0">
              <v-btn
                color="primary"
                class="mr-4"
                @click="$router.push({ name: 'order.index' })"
              >
                {{ $t("back") }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                v-if="allowEdit"
                color="primary"
                class="mr-4"
                @click="saveItem"
              >
                {{ $t("save") }}
              </v-btn>
              <template v-if="formValues.company && formValues.company.active">
                <v-btn
                  v-if="formValues.state_id == 1 && orderLines.length > 0"
                  color="primary"
                  class="mr-4"
                  @click="submitOrder"
                >
                  {{ $t("submit order") }}
                </v-btn>
                <v-btn
                  v-if="
                    formValues.state_id == 2 &&
                      this.$auth
                        .user()
                        .permissions.includes('labelEntry.create')
                  "
                  color="primary"
                  class="mr-4"
                  @click="confirmOrder"
                >
                  {{ $t("confirm order") }}
                </v-btn>
                <v-btn
                  v-if="
                    formValues.state_id == 2 &&
                      this.$auth
                        .user()
                        .permissions.includes('labelEntry.create')
                  "
                  color="primary"
                  class="mr-4"
                  @click="declineOrder"
                >
                  {{ $t("decline order") }}
                </v-btn>
              </template>
            </v-card-actions>
            <template v-if="formValues.company && !formValues.company.active">
              <v-row>
                <v-col>
                  <v-alert dense prominent type="error">
                    {{ $t("account_inactive") }}
                  </v-alert>
                </v-col>
              </v-row>
            </template>
          </v-form>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  saveItem,
  fetchItem,
  fetchCustomers,
  fetchCountries,
  submitOrder,
  fetchOrderItems,
  confirmOrder,
  declineOrder,
  savePayment,
  setPayedAmount,
  setOrderSend,
  fetchHandbooks,
  regenerateDocument,
  generateDeliveryNote as requestGenerateDeliveryNote,
  fetchOrderlines
} from "../shared/services";
import { apiConstants } from "../shared/config";
import { apiPost } from "../../shared/services";
import Orderlines from "../list/orderlines";
import { fetchSuppliers } from "../../items/shared/services";

export default {
  name: "app-order-item",
  components: {
    Orderlines
  },
  $_veeValidate: {
    validator: "new"
  },
  data() {
    return {
      formValues: {},
      permissions: [],
      loading: true,
      loading_articles: true,
      loading_handbooks: true,
      tab: 0,
      tabs: ["Orderdata", "Preset", "Orderlines"],
      customers: [],
      countries: [],
      orderItems: [],
      suppliers: [],
      orderLines: [],
      handbooks: [],
      production_notifications: [],
      paymentDialog: false,
      paymentPositions: [],
      paymentLoading: false,
      generatingDeliveryNote: false,
      deliveryNoteExists: false,
      payPostCosts: false,
      paymentAmountDialog: false,
      payed_amount: 0,
      postCosts: false,
      printCosts: false,

      visible_sketches: [],
      index_sketches: [],
      visible_images: [],
      index_images: []
    };
  },
  mounted() {
    this.init();
    fetchCustomers().then(res => {
      this.customers = res.data;
    });
    fetchCountries().then(res => {
      this.countries = res.data;
    });
    fetchSuppliers().then(res => {
      this.suppliers = res.data;
    });
    if (this.$route.params.id) {
      this.getHandbooks();
    }
  },
  computed: {
    computedFormValues() {
      delete this.formValues.customer;

      return {
        ...this.formValues
      };
    },
    computedDocuments() {
      var documents = [];
      this.orderLines.forEach(function(item) {
        var actual = item.door_type.documents.filter(
          doc => doc.type == "documents"
        );
        var position = item.position;
        actual.forEach(function(item) {
          if (documents[item.id] === undefined) {
            item.positions = [position];
            documents[item.id] = item;
          } else {
            documents[item.id].positions.push(position);
          }
        });
      });
      let tmp = [];
      documents.forEach(function(item) {
        tmp.push(item);
      });

      return tmp;
    },
    computedDocumentsAbroad() {
      var documents = [];
      this.orderLines.forEach(function(item) {
        var actual = item.door_type.documents.filter(
          doc => doc.type == "documents_abroad"
        );
        var position = item.position;
        actual.forEach(function(item) {
          if (documents[item.id] === undefined) {
            item.positions = [position];
            documents[item.id] = item;
          } else {
            documents[item.id].positions.push(position);
          }
        });
      });
      let tmp = [];
      documents.forEach(function(item) {
        tmp.push(item);
      });

      return tmp;
    },
    computedDrawings() {
      var documents = [];
      this.orderLines.forEach(function(item) {
        var actual = item.door_type.documents.filter(
          doc => doc.type == "drawings"
        );
        var position = item.position;
        actual.forEach(function(item) {
          if (documents[item.id] === undefined) {
            item.positions = [position];
            documents[item.id] = item;
          } else {
            documents[item.id].positions.push(position);
          }
        });
      });
      let tmp = [];
      documents.forEach(function(item) {
        tmp.push(item);
      });

      return tmp;
    },
    labelDelivery() {
      return [
        {
          name: this.$t("label_send"),
          id: 1,
          selected: true
        },
        {
          name: this.$t("label_take"),
          id: 0
        }
      ];
    },
    paymentHeaders() {
      return [
        {
          text: this.$t("description"),
          align: "left",
          value: "description"
        },
        {
          text: this.$t("quantity"),
          align: "left",
          value: "quantity"
        },
        {
          text: this.$t("price"),
          align: "left",
          value: "price"
        },
        {
          text: this.$t("total_price"),
          align: "left",
          value: "totalPrice"
        }
      ];
    },
    paymentTotals() {
      let amount = 0;
      this.paymentPositions.map(position => {
        amount += position.price * position.quantity;
      });
      return amount;
    },
    // Haustüren have a label entry without labelType (CE certificate, no plaque)
    printCostQuantity() {
      if (!this.orderLines) {
        return 0;
      }
      return this.orderLines.filter(orderLine => {
        return !(orderLine.label_entry && orderLine.label_entry.labelType);
      }).length;
    },
    allowEdit() {
      if (this.formValues.id) {
        if (this.formValues.state_id == 1) {
          return true;
        }
        if (this.$auth.user().permissions.includes("order.editall")) {
          return true;
        }
        return false;
      }
      return true;
    },
    hasConformity() {
      let result = this.orderLines.find(orderline => {
        return orderline.certificate_type.find(type => {
          return (
            type.country_id == this.formValues.country_id &&
            type.certificate_type == "standard"
          );
        });
      });

      if (result) {
        return true;
      } else {
        return false;
      }
    },
    hasMoraltConformity() {
      let result = this.orderLines.find(orderline => {
        return orderline.certificate_type.find(type => {
          return (
            type.country_id == this.formValues.country_id &&
            type.certificate_type == "moralt"
          );
        });
      });

      if (result) {
        return true;
      } else {
        return false;
      }
    },
    hasProductionNotification() {
      let result = this.orderLines.find(orderline => {
        return orderline.production_notification;
      });

      if (result) {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    init() {
      if (this.$route.params.id) {
        this.loading = true;
        return fetchItem(this.$route.params.id).then(res => {
          this.formValues = res.data;
          this.syncDeliveryNoteExists();
          this.loading = false;
        });
      }
      this.loading = false;
      return Promise.resolve();
    },
    syncDeliveryNoteExists() {
      const documents = (this.formValues && this.formValues.documents) || [];
      this.deliveryNoteExists = documents.some(
        doc => doc.name === "delivery-note"
      );
    },
    async submitOrder() {
      if (
        await this.$root.$confirm(
          this.$t("Send Order"),
          this.$t("Send Order Info")
        )
      ) {
        let save = await this.saveItem(true);
        if (save) {
          let resp = await submitOrder(
            this.$route.params.id,
            null,
            this.$validator
          );
          if (resp.success) {
            this.$root.$notification(resp.message, { type: "success" });
            this.$router.push({ name: "order.index" });
          }
        }
      }
    },
    async confirmOrder() {
      let save = await this.saveItem(true);
      if (save) {
        let resp = await confirmOrder(
          this.$route.params.id,
          null,
          this.$validator
        );
        if (resp.success) {
          this.$root.$notification(resp.message, { type: "success" });
          this.$router.push({ name: "order.index" });
        } else {
          this.$root.$notification(resp.message, { type: "error" });
        }
      }
    },
    async declineOrder() {
      let save = await this.saveItem(true);
      if (save) {
        let resp = await declineOrder(
          this.$route.params.id,
          null,
          this.$validator
        );
        if (resp.success) {
          this.$root.$notification(resp.message, { type: "success" });
          this.$router.push({ name: "order.index" });
        }
      }
    },

    async createPayment() {
      this.paymentDialog = true;
      this.paymentPositions = [];
      this.paymentLoading = true;
      try {
        // Prefer already synced lines; fetch only if still empty
        if (!this.orderLines || !this.orderLines.length) {
          const response = await fetchOrderlines(
            { itemsPerPage: -1, page: 1 },
            this.$route.params.id
          );
          this.orderLines = response.data || [];
        }
        if (this.orderLines) {
          this.orderLines.map(orderLine => {
            if (orderLine.label_entry && orderLine.label_entry.labelType) {
              this.addPaymentLine(orderLine.label_entry.labelType);
            }
          });
        }
        //check if there are existing payments and remove them
        this.formValues.payment_positions.forEach(payment => {
          let index = this.paymentPositions.findIndex(
            bookedpayment =>
              bookedpayment.label_type_id == payment.label_type_id
          );
          if (index !== -1) {
            if (this.paymentPositions[index].quantity - payment.quantity > 0) {
              //subtract
              this.paymentPositions[index].quantity -= payment.quantity;
            } else {
              //all payed - remove from index
              this.paymentPositions.splice(index, 1);
            }
          }
        });
        // Add extras directly — do not call toggle* while setting v-switch
        // (programmatic v-model + @change would add then immediately remove)
        if (this.formValues && this.formValues.label_delivery) {
          this.addPostCostsLine();
          this.postCosts = true;
        } else {
          this.postCosts = false;
        }
        if (this.printCostQuantity > 0) {
          this.addPrintCostsLine();
          this.printCosts = true;
        } else {
          this.printCosts = false;
        }
      } finally {
        this.paymentLoading = false;
      }
    },
    async savePayment() {
      let save = true;
      if (this.formValues.state_id <= 2) {
        save = await this.saveItem(true);
      }
      if (save) {
        let resp = await savePayment(
          this.$route.params.id,
          { paymentpositions: this.paymentPositions },
          this.$validator
        );
        if (resp.success) {
          this.$root.$notification(resp.message, { type: "success" });
          this.paymentDialog = false;
          this.init();
        }
      }
    },
    openPaymentAmountDialog() {
      this.payed_amount =
        this.formValues.payed_amount > 0
          ? this.formValues.payed_amount
          : this.formValues.total_price;
      this.paymentAmountDialog = true;
    },
    async savePaymentAmount() {
      const raw = String(this.payed_amount == null ? "" : this.payed_amount);
      const payed_amount = parseFloat(raw.replace(/\s/g, "").replace(",", "."));
      if (Number.isNaN(payed_amount)) {
        this.$root.$notification(
          this.$t("Invalid amount") || "Ungültiger Betrag",
          {
            type: "error"
          }
        );
        return;
      }
      try {
        let resp = await setPayedAmount(
          this.$route.params.id,
          { payed_amount },
          this.$validator
        );
        if (resp && resp.success) {
          this.paymentAmountDialog = false;
          this.$root.$notification(resp.message, { type: "success" });
          this.init();
        }
      } catch (e) {
        const message =
          (e.response && e.response.data && e.response.data.message) ||
          this.$t("error") ||
          "Fehler beim Einbuchen";
        this.$root.$notification(message, { type: "error" });
      }
    },
    async setOrderSend() {
      let resp = await setOrderSend(this.$route.params.id);
      if (resp.success) {
        this.$root.$notification(resp.message, { type: "success" });
        this.$router.push({ name: "order.index" });
      }
    },
    addPaymentLine(paymentLine) {
      let paymentPosition = this.paymentPositions.find(position => {
        return position.label_type_id == paymentLine.id;
      });
      if (paymentPosition) {
        paymentPosition.quantity += 1;
        paymentPosition.totalPrice =
          paymentPosition.quantity * paymentPosition.price;
      } else {
        this.paymentPositions.push({
          label_type_id: paymentLine.id,
          quantity: 1,
          price: paymentLine.costs,
          totalPrice: paymentLine.costs,
          description: paymentLine.name,
          editable: false,
          type: "label"
        });
      }
    },
    addPostCostsLine() {
      if (this.paymentPositions.find(p => p.type == "postcosts")) {
        return;
      }
      this.paymentPositions.push({
        label_type_id: "postcosts",
        quantity: 1,
        price: 6.8,
        totalPrice: 6.8,
        description: this.$t("post_costs"),
        editable: true,
        quantity_editable: false,
        type: "postcosts"
      });
    },
    removePostCostsLine() {
      this.paymentPositions = this.paymentPositions.filter(position => {
        return position.type != "postcosts";
      });
    },
    onPostCostsChange(enabled) {
      if (enabled) {
        this.addPostCostsLine();
      } else {
        this.removePostCostsLine();
      }
    },
    togglePostCosts() {
      this.onPostCostsChange(
        !this.paymentPositions.find(p => p.type == "postcosts")
      );
    },
    addPostCosts() {
      this.addPostCostsLine();
    },
    addPrintCostsLine() {
      if (this.paymentPositions.find(p => p.type == "printcosts")) {
        return;
      }
      const quantity = this.printCostQuantity || 1;
      this.paymentPositions.push({
        label_type_id: "printcosts",
        quantity: quantity,
        price: 10,
        totalPrice: 10 * quantity,
        description: this.$t("print_costs"),
        editable: true,
        quantity_editable: true,
        type: "printcosts"
      });
    },
    removePrintCostsLine() {
      this.paymentPositions = this.paymentPositions.filter(position => {
        return position.type != "printcosts";
      });
    },
    onPrintCostsChange(enabled) {
      if (enabled) {
        this.addPrintCostsLine();
      } else {
        this.removePrintCostsLine();
      }
    },
    togglePrintCosts() {
      this.onPrintCostsChange(
        !this.paymentPositions.find(p => p.type == "printcosts")
      );
    },
    async saveDownloaded() {
      this.$validator.reset();
      this.$validator.validateAll();
      let resp = await saveItem(
        this.$route.params.id,
        this.computedFormValues,
        this.$validator
      );
      if (resp.success) {
        this.$root.$notification(resp.message, { type: "success" });
      } else {
        return Promise.reject();
      }
    },
    async saveItem(remain = false) {
      let save = false;
      if (this.formValues.state_id > 2) {
        if (
          await this.$root.$confirm(
            this.$t("save"),
            this.$t(
              "This order was already confirmed - are you sure to save this change?"
            )
          )
        ) {
          save = true;
        }
      } else {
        save = true;
      }

      if (save) {
        this.$validator.reset();
        this.$validator.validateAll();
        let resp = await saveItem(
          this.$route.params.id,
          this.computedFormValues,
          this.$validator
        );
        if (resp.success) {
          if (remain) {
            if (!this.$route.params.id) {
              this.$router.push({
                name: "order.edit",
                params: { id: resp.data.id }
              });
              this.init();
            }
            this.$root.$notification(resp.message, { type: "success" });
            return Promise.resolve(true);
          } else {
            this.$root.$notification(resp.message, { type: "success" });
            this.$router.push({ name: "order.index" });
          }
        } else {
          return Promise.reject();
        }
      }
    },
    getOrderItems() {
      this.loading_articles = true;
      fetchOrderItems(this.$route.params.id).then(res => {
        this.orderItems = res;
        this.orderItems.forEach(function(item, index) {
          this.visible_sketches[index] = false;
          this.visible_images[index] = false;
          this.index_sketches[index] = 0;
          this.index_images[index] = 0;
        }, this);
        this.loading_articles = false;
      });
    },
    getHandbooks() {
      this.loading_handbooks = true;
      fetchHandbooks(this.$route.params.id).then(res => {
        this.handbooks = res.handbooks;
        this.production_notification = res.production_notification;
        this.loading_handbooks = false;
      });
    },
    changeCustomer() {
      let customer = this.customers.find(customer => {
        return customer.id == this.formValues.customer_id;
      });
      if (customer) {
        this.$set(this.formValues, "project", customer.name);
        this.$set(
          this.formValues,
          "reference_number_customer",
          customer.number
        );

        let address = "";
        if (customer.street) {
          address += customer.street;
        }
        if (address.length) {
          if (customer.zip.length || customer.city) {
            address += ", ";
          }
        }
        address += customer.zip + " " + customer.city;
        this.$set(this.formValues, "project_address", address);
      }
    },
    showImgSketches(index, i) {
      this.$set(this.index_sketches, index, i);
      this.$set(this.visible_sketches, index, true);
    },
    showImgImages(index, i) {
      this.$set(this.index_images, index, i);
      this.$set(this.visible_images, index, true);
    },
    handleHide(index) {
      this.$set(this.visible_sketches, index, false);
      this.$set(this.visible_images, index, false);
    },
    async regenerateDocument(id) {
      let resp = await regenerateDocument(id);
      if (resp.success) {
        this.$root.$notification(resp.message, { type: "success" });
      } else {
        this.$root.$notification(resp.message, { type: "error" });
      }
      this.init();
    },
    async generateDeliveryNote() {
      this.generatingDeliveryNote = true;
      try {
        let resp = await requestGenerateDeliveryNote(this.formValues.id);
        if (resp.success) {
          this.$root.$notification(resp.message, { type: "success" });
        } else {
          this.$root.$notification(resp.message, { type: "error" });
        }
        await this.init();
      } finally {
        this.generatingDeliveryNote = false;
      }
    },
    toggleDocumentInternal(id, internal) {
      apiPost(apiConstants.orderDocument + "/" + id + "/setinternal", {
        internal
      }).then(resp => {
        if (resp.success) {
          this.$root.$notification(resp.message, { type: "success" });
        } else {
          this.$root.$notification(resp.message, { type: "error" });
        }
        this.init();
      });
    },
    addToken(item) {
      return item.sketches.map(sketch =>
        sketch.src
          ? { ...sketch, src: this.$options.filters.addToken(sketch.src) }
          : sketch
      );
    }
  },
  watch: {
    hasProductionNotification: {
      handler() {
        if (this.formValues.mounting_person_name == null) {
          this.formValues.mounting_person_name = this.formValues.company.mounting_person_name;
        }
        if (this.formValues.mounting_person_phone == null) {
          this.formValues.mounting_person_phone = this.formValues.company.mounting_person_phone;
        }
        if (this.formValues.processing_person_name == null) {
          this.formValues.processing_person_name = this.formValues.company.processing_person_name;
        }
        if (this.formValues.processing_person_phone == null) {
          this.formValues.processing_person_phone = this.formValues.company.processing_person_phone;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
